import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  HStack,
  Icon
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { QueryTrainsApi } from '../../../../apis/QueryMaintenance.api';
import {
  chooseDataStateType,
  MaintenanceModalDispLabels
} from '../../../molecules/MaintenanceModalDispLabels';
import { MaintenanceFooter } from '../MaintenanceFooter';
import {
  ArrowUpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@chakra-ui/icons';
import { TrainsSearchCondition } from '../../../../pages/TrainsMaintenance';
import { MaintenanceCompareEnum } from '../../../constants/MaintenanceConstants';

const TableColumnEnum = {
  id: 'id',
  no: 'no',
  train_group_name: 'train_group_name',
  created_at: 'created_at',
  updated_at: 'updated_at'
};

export interface MainteTrainsType {
  id: number;
  no: number;
  train_group_name: string;
  created_at: string;
  updated_at: string;
}

interface props {
  TrainsDatas: MainteTrainsType[] | undefined;
  setMaintenanceDatas: React.Dispatch<
    React.SetStateAction<MainteTrainsType[] | undefined>
  >;
  setChooseData: (data: chooseDataStateType) => void;
  onOpenBrowse: () => void;
  onOpenEdit: () => void;
  onToggle: () => void;
  Query: TrainsSearchCondition;
  setQuery: React.Dispatch<React.SetStateAction<TrainsSearchCondition>>;
  createOrUpdate: boolean;
  searchSwitch: boolean;
}

const headers = [
  { label: 'Id', key: 'id' },
  { label: 'No', key: 'no' },
  { label: 'Model', key: 'train_group_name' },
  { label: 'Created At', key: 'created_at' },
  { label: 'Updated At', key: 'updated_at' }
];

const GetCsvFileName = () => {
  const date = new Date();
  const hour =
    date.getMonth().toString().length === 1
      ? '0' + (date.getMonth() + 1).toString()
      : date.getMonth().toString();
  const day =
    date.getDate().toString().length === 1
      ? '0' + date.getDate().toString()
      : date.getDate().toString();
  return 'trains-' + date.getFullYear() + '-' + hour + '-' + day;
};

export const MaintenanceTrainsTable: React.FC<props> = (props) => {
  const {
    TrainsDatas,
    setMaintenanceDatas,
    setChooseData,
    onOpenBrowse,
    onOpenEdit,
    onToggle,
    Query,
    setQuery,
    createOrUpdate,
    searchSwitch
  } = props;

  const GetDateStringForCsv = (dateString: string): string => {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(5, 7);
    const day = dateString.substring(8, 10);
    const hour = dateString.substring(12, 14);
    const minute = dateString.substring(15, 17);
    return `${year}-${month}-${day} ${hour}:${minute}:00 +0900`;
  };
  const csvData = TrainsDatas?.map((traindata) => {
    const traindataForCsv: MainteTrainsType = {
      id: traindata.id,
      no: traindata.no,
      train_group_name: traindata.train_group_name.replace('系', ''),
      created_at: GetDateStringForCsv(traindata.created_at),
      updated_at: GetDateStringForCsv(traindata.updated_at)
    };
    return traindataForCsv;
  });
  const [pageNow, setPageNow] = useState<number>(1);
  const [columnSortType, setColumnSortType] = useState<{
    key: string;
    sort: boolean;
  }>({ key: '', sort: false });
  const pageDispMax = 30;
  const pageMax = 6;
  const csvFileName = GetCsvFileName();
  const GetPageCount = (): number => {
    if (TrainsDatas !== undefined) {
      const divisionCount = Math.floor(TrainsDatas.length / pageDispMax);
      const remCount = TrainsDatas.length % pageDispMax === 0 ? 0 : 1;
      return divisionCount + remCount;
    }
    return 1;
  };
  const ColumnNameTriangleType = (column: string) => {
    if (columnSortType.key === column) {
      if (columnSortType.sort) {
        return TriangleUpIcon;
      } else {
        return TriangleDownIcon;
      }
    }
    return ArrowUpDownIcon;
  };

  const ColumnSortApi = async (column: string) => {
    const params = new URLSearchParams();
    const sortType = !columnSortType.sort;
    params.append('order', column + (sortType ? '_asc' : '_desc'));

    if (Query.trainNumber.number !== '') {
      switch (Query.trainNumber.compare) {
        case MaintenanceCompareEnum.Equal:
          params.append('no_eq', Query.trainNumber.number);
          break;
        case MaintenanceCompareEnum.Greater:
          params.append('no_gt', Query.trainNumber.number);
          break;
        case MaintenanceCompareEnum.Less:
          params.append('no_lt', Query.trainNumber.number);
          break;
      }
    }
    Query.model !== '' && params.append('train_group_id_eq', Query.model);
    Query.dateCreatedFrom !== '' &&
      params.append('created_at_gte', Query.dateCreatedFrom);
    Query.dateCreatedTo !== '' &&
      params.append('created_at_lt', Query.dateCreatedTo);
    Query.dateUpdatedFrom !== '' &&
      params.append('updated_at_gte', Query.dateUpdatedFrom);
    Query.dateUpdatedTo !== '' &&
      params.append('updated_at_lt', Query.dateUpdatedTo);
    const query = '/?' + params.toString();

    setQuery((pre) => ({ ...pre, sort: { key: column, sortType: sortType } }));

    const response = await QueryTrainsApi(query);
    if (response.success && response.data !== null) {
      setMaintenanceDatas(response.data as MainteTrainsType[]);
      setColumnSortType({ key: column, sort: sortType });
    } else {
      alert(column + 'のソートに失敗しました。');
    }
  };

  useEffect(() => {
    setPageNow(1);
  }, [searchSwitch]);

  useEffect(() => {
    setPageNow(1);
    setColumnSortType({ key: '', sort: false });
  }, [createOrUpdate]);

  return (
    <>
      <TableContainer
        position='absolute'
        h='100%'
        w='100%'
        ml={5}
        overflowY='auto'
        gridRow='2/3'
        gridColumn='1/2'
        overflowX='unset'
      >
        <Table variant='simple'>
          <Thead borderWidth='1px' position='sticky' top={0} zIndex='docked'>
            <Tr bg='gray.800'>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.id)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>ID</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.id)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.no)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>車号</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.no)}
                  />
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>形式</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>登録日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>更新日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'></Th>
            </Tr>
          </Thead>
          <Tbody>
            {TrainsDatas !== undefined &&
              TrainsDatas.map((data, i) => {
                if (
                  pageDispMax * pageNow - pageDispMax <= i &&
                  i < pageDispMax * pageNow
                ) {
                  return (
                    <Tr key={`${'trains_' + i}`}>
                      <Td>{data.id}</Td>
                      <Td>{data.no}</Td>
                      <Td>{data.train_group_name}</Td>
                      <Td>{data.created_at}</Td>
                      <Td>{data.updated_at}</Td>
                      <Td>
                        <MaintenanceModalDispLabels
                          data={{ type: 'trains', trains: data }}
                          setChooseData={setChooseData}
                          onOpenBrowse={onOpenBrowse}
                          onOpenEdit={onOpenEdit}
                          onToggle={onToggle}
                        />
                      </Td>
                    </Tr>
                  );
                }
              })}
          </Tbody>
        </Table>
      </TableContainer>
      <MaintenanceFooter
        CsvData={csvData !== undefined ? csvData : []}
        DataLength={TrainsDatas?.length}
        Headers={headers}
        CsvFileName={csvFileName}
        GetPageCount={GetPageCount}
        pageMax={pageMax}
        pageNow={pageNow}
        setPageNow={setPageNow}
      />
    </>
  );
};
